import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Briefcase, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import EstimationBanner from '../../components/EstimationBanner';
import OptimizedImage from '../../components/OptimizedImage';

import SEO from '../../components/SEO';

const ServiceProfessionnel = () => {
  const advantages = [
    'Mobilier de bureau et rangements',
    'Matériel informatique obsolète',
    'Archives et documentation',
    'Intervention hors horaires si besoin',
    'Respect de la confidentialité',
    'Devis gratuit et sans engagement'
  ];

  const clients = [
    {
      title: 'Bureaux et entreprises',
      description: 'Débarras de mobilier de bureau, postes de travail, salles de réunion lors de déménagements ou rénovations.'
    },
    {
      title: 'Commerces',
      description: 'Vidage de magasins, rayonnages, vitrines et équipements commerciaux lors de fermetures ou changements d\'activité.'
    },
    {
      title: 'Entrepôts et locaux industriels',
      description: 'Évacuation de matériel, stocks obsolètes, machines et équipements industriels.'
    },
    {
      title: 'Cabinets médicaux et libéraux',
      description: 'Débarras discret et confidentiel de mobilier médical, archives et équipements.'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Débarras professionnel : bureaux, commerces | DEBECO"
        description="Débarras de locaux professionnels, bureaux, commerces et archives en Savoie. Confidentialité garantie, intervention hors horaires possible."
        canonical="/services/professionnel"
        ogImage="/images/professionnel.jpg"
        jsonLd={[
          {"@context":"https://schema.org","@type":"Service","name":"Débarras de locaux professionnels","description":"Débarras de bureaux, commerces, entrepôts et archives. Confidentialité garantie, intervention possible hors horaires.","provider":{"@type":"LocalBusiness","name":"DEBECO","telephone":"+33451401617","address":{"@type":"PostalAddress","addressLocality":"Annecy","postalCode":"74600","addressCountry":"FR"}},"areaServed":[{"@type":"AdministrativeArea","name":"Haute-Savoie"},{"@type":"AdministrativeArea","name":"Savoie"},{"@type":"AdministrativeArea","name":"Ain"}]},
          {"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Accueil","item":"https://debeco.fr/"},{"@type":"ListItem","position":2,"name":"Services","item":"https://debeco.fr/services"},{"@type":"ListItem","position":3,"name":"Débarras professionnel"}]}
        ]}
      />
      {/* Hero */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Débarras professionnel</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Bureaux, commerces, locaux, archives
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
              {/* Image */}
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="/images/professionnel.jpg"
                  webpSrc="/images/professionnel.webp"
                  alt="Bureau professionnel avec mobilier à débarrasser, espace de travail avant intervention DEBECO"
                  className="w-full h-full object-cover"
                  width="800"
                  height="534"
                  loading="eager"
                  data-testid="service-professionnel-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    <Briefcase className="w-10 h-10 text-blue-900" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Service dédié aux professionnels</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  DEBECO accompagne les professionnels dans leurs besoins de débarras : déménagement de bureaux, fermeture de commerces, rénovation de locaux, gestion de stocks obsolètes. Nous comprenons les contraintes spécifiques du monde professionnel.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  Nous pouvons intervenir en dehors des horaires d'ouverture pour ne pas perturber votre activité, et nous garantissons la confidentialité des documents et données sensibles. Tout le matériel informatique est traité selon les normes en vigueur.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact">
                    <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg w-full sm:w-auto">
                      Demander un devis gratuit
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0451401617">
                    <Button variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white w-full sm:w-auto">
                      <Phone className="mr-2 w-4 h-4" />
                      04 51 40 16 17
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Archives Section */}
            <Card className="border-2 border-blue-900 bg-gradient-to-br from-blue-50 to-blue-100 mb-16">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-900 rounded-xl flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-blue-900 mb-3">Débarras d'archives et destruction sécurisée</h3>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      Nous proposons un service spécialisé pour le débarras de vos archives professionnelles. Qu'il s'agisse de documents comptables, de dossiers clients ou de tout autre document confidentiel, nous assurons leur destruction sécurisée et confidentielle.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700"><strong>Collecte sécurisée</strong> de vos documents confidentiels</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700"><strong>Destruction confidentielle</strong> dans le respect des normes RGPD</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700"><strong>Certificat de destruction</strong> sur demande</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Advantages */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Ce que nous prenons en charge</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {advantages.map((advantage, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{advantage}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Clients */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Nos types de clients</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {clients.map((client, index) => (
                  <Card key={index} className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-xl text-blue-900 mb-3">{client.title}</h3>
                      <p className="text-gray-600">{client.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Estimation Banner */}
      <EstimationBanner />

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Process */}
            <Card className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-green-50">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-blue-900 mb-6 text-center">Notre engagement professionnel</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {[
                    { step: '1', title: 'Discrétion', desc: 'Respect de votre activité et de vos horaires' },
                    { step: '2', title: 'Confidentialité', desc: 'Protection des données sensibles' },
                    { step: '3', title: 'Efficacité', desc: 'Intervention rapide et organisée' },
                    { step: '4', title: 'Conformité', desc: 'Traitement selon les normes' }
                  ].map((item) => (
                    <div key={item.step} className="text-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                        <span className="text-xl font-bold text-white">{item.step}</span>
                      </div>
                      <h3 className="font-bold text-blue-900 mb-1">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="py-12 bg-gray-50" data-testid="maillage-interne">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Nos autres services de débarras</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/services/succession" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras après succession</p>
              <p className="text-sm text-gray-600 mt-1">Pour les biens professionnels hérités ou transmis</p>
            </Link>
            <Link to="/zones" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Nos zones d'intervention</p>
              <p className="text-sm text-gray-600 mt-1">Savoie, Haute-Savoie et Ain</p>
            </Link>
            <Link to="/devis" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Estimation gratuite en ligne</p>
              <p className="text-sm text-gray-600 mt-1">Obtenez un devis en quelques minutes</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Besoin d'un débarras professionnel ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour un devis gratuit adapté à vos besoins professionnels
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto">
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceProfessionnel;
